// Outbox event contract [02 §5.2]: the row shape is `outbox_event(id uuidv7, aggregate_type,
// aggregate_id, event_type, schema_version, payload jsonb, occurred_at, actor_id, request_id,
// published_at)`. `published_at` belongs to the relay, not to the event, so it is not part of the
// envelope. Payload schemas are versioned; consumers are idempotent on `id`.
import { z } from 'zod';

import { idSchema, uuidV7Schema } from '../kernel/index.ts';
import { documentTypeSchema } from '../dto/common.ts';
import { utcTimestampSchema } from '../dto/scalars.ts';

/** `<context>.<aggregate>.<verb>` in snake_case, e.g. `sales.order.confirmed`. */
export const EVENT_TYPE_PATTERN = /^[a-z][a-z0-9]*\.[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;

export interface EventDefinition<
  T extends string = string,
  V extends number = number,
  P extends z.ZodType = z.ZodType,
  A extends string = string,
> {
  readonly type: T;
  readonly version: V;
  /** `outbox_event.aggregate_type`, the owning aggregate's table name. */
  readonly aggregateType: A;
  readonly payload: P;
  /** Where the spec names this event (traceability for AC-KRN-05). */
  readonly specRefs: readonly string[];
}

export function defineEvent<
  const T extends string,
  const V extends number,
  P extends z.ZodType,
  const A extends string,
>(definition: EventDefinition<T, V, P, A>): EventDefinition<T, V, P, A> {
  if (!EVENT_TYPE_PATTERN.test(definition.type))
    throw new TypeError(`event type '${definition.type}' must match <context>.<aggregate>.<verb>`);
  if (!Number.isInteger(definition.version) || definition.version < 1)
    throw new TypeError(`event '${definition.type}' needs a positive integer version`);
  return Object.freeze({ ...definition, specRefs: Object.freeze([...definition.specRefs]) });
}

export const outboxEnvelopeSchema = z.strictObject({
  id: uuidV7Schema,
  aggregate_type: documentTypeSchema,
  aggregate_id: idSchema,
  event_type: z.string().regex(EVENT_TYPE_PATTERN),
  schema_version: z.int().positive(),
  payload: z.unknown(),
  occurred_at: utcTimestampSchema,
  /** null for system jobs (schedules, relay, migration). */
  actor_id: idSchema.nullable(),
  request_id: uuidV7Schema.nullable(),
  /** Relay bookkeeping: present on rows read from the table, never set by producers. */
  published_at: utcTimestampSchema.nullable().optional(),
});

type EnvelopeHead = Omit<
  z.output<typeof outboxEnvelopeSchema>,
  'aggregate_type' | 'event_type' | 'schema_version' | 'payload' | 'published_at'
>;

export type OutboxEventOf<D extends EventDefinition> = EnvelopeHead & {
  readonly aggregate_type: D['aggregateType'];
  readonly event_type: D['type'];
  readonly schema_version: D['version'];
  readonly payload: z.output<D['payload']>;
};

/** What a producer supplies; type, version and aggregate type come from the definition. */
export type OutboxEventInput<D extends EventDefinition> = {
  readonly id: string;
  readonly aggregate_id: string;
  readonly occurred_at: string;
  readonly actor_id: string | null;
  readonly request_id: string | null;
  readonly payload: z.input<D['payload']>;
};

/** Builds a validated event for the outbox insert (same transaction as the command, BR-ARC-006). */
export function buildOutboxEvent<D extends EventDefinition>(
  definition: D,
  input: OutboxEventInput<D>,
): OutboxEventOf<D> {
  const envelope = outboxEnvelopeSchema.parse({
    ...input,
    aggregate_type: definition.aggregateType,
    event_type: definition.type,
    schema_version: definition.version,
  });
  return {
    ...envelope,
    event_type: definition.type,
    schema_version: definition.version,
    payload: definition.payload.parse(input.payload) as z.output<D['payload']>,
  };
}
